import { Component, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { EChartsOption } from 'echarts';
import { RapportsService, FiltreRapport } from '../rapports.service';
import { FiltresCommunsRapports } from '../rapports.component';

@Component({
  selector: 'app-rapport-qualite',
  templateUrl: './rapport-qualite.component.html'
})
export class RapportQualiteComponent implements OnInit, OnChanges {
  @Input() filtres!: FiltresCommunsRapports;

  chargement = { erreurs: true, pertes: true, dommages: true, reclamations: true };

  erreurs: any   = null;
  pertes: any    = null;
  dommages: any  = null;
  reclamations: any = null;

  filtrePriorite: string | null = null;
  filtreLivreurId: number | null = null;

  optErreursEvolution: EChartsOption = {};
  optErreursTopTrajets: EChartsOption = {};
  optPertesEvolution: EChartsOption = {};
  optDommagesEvolution: EChartsOption = {};
  optDommagesParPriorite: EChartsOption = {};
  optReclamationsTopMotifs: EChartsOption = {};
  optReclamationsEvolution: EChartsOption = {};

  constructor(private service: RapportsService) {}

  ngOnInit(): void { this.charger(); }
  ngOnChanges(_changes: SimpleChanges): void { this.charger(); }

  private filtreApi(extra: Partial<FiltreRapport> = {}): FiltreRapport {
    return {
      debut: this.filtres.debut,
      fin: this.filtres.fin,
      zoneId: this.filtres.zoneId,
      typeEnvoi: this.filtres.typeEnvoi,
      ...extra
    };
  }

  charger(): void {
    this.chargerErreurs();
    this.chargerPertes();
    this.chargerDommages();
    this.chargerReclamations();
  }

  private chargerErreurs(): void {
    this.chargement.erreurs = true;
    this.service.erreursAcheminement(this.filtreApi()).subscribe({
      next: (d) => {
        this.erreurs = d;
        this.optErreursEvolution = this.lineFromMap(d.evolutionMensuelle, 'Erreurs', '#dc2626');
        this.optErreursTopTrajets = this.barHorizontalFromList(d.topTrajets, 'label', 'valeur', 'Top trajets erronés', '#f59e0b');
        this.chargement.erreurs = false;
      },
      error: () => this.chargement.erreurs = false
    });
  }

  private chargerPertes(): void {
    this.chargement.pertes = true;
    this.service.pertesVols(this.filtreApi({ livreurId: this.filtreLivreurId })).subscribe({
      next: (d) => {
        this.pertes = d;
        this.optPertesEvolution = this.lineFromMap(d.evolutionMensuelle, 'Pertes + Vols', '#7c3aed');
        this.chargement.pertes = false;
      },
      error: () => this.chargement.pertes = false
    });
  }

  private chargerDommages(): void {
    this.chargement.dommages = true;
    this.service.colisEndommages(this.filtreApi({ priorite: this.filtrePriorite })).subscribe({
      next: (d) => {
        this.dommages = d;
        this.optDommagesEvolution = this.lineFromMap(d.evolutionMensuelle, 'Colis endommagés', '#f97316');
        this.optDommagesParPriorite = this.pieFromMap(d.repartitionParPriorite, 'Par priorité');
        this.chargement.dommages = false;
      },
      error: () => this.chargement.dommages = false
    });
  }

  private chargerReclamations(): void {
    this.chargement.reclamations = true;
    this.service.tauxReclamations(this.filtreApi()).subscribe({
      next: (d) => {
        this.reclamations = d;
        const topMotifs = (d.topMotifs || []).map((m: any) => ({ name: m.label, value: m.valeur }));
        this.optReclamationsTopMotifs = {
          tooltip: { trigger: 'item' },
          legend: { bottom: 0, type: 'scroll' },
          series: [{
            type: 'pie',
            radius: ['40%', '70%'],
            avoidLabelOverlap: true,
            itemStyle: { borderRadius: 6, borderColor: '#fff', borderWidth: 2 },
            label: { show: false }, labelLine: { show: false },
            data: topMotifs
          }]
        };
        this.optReclamationsEvolution = this.lineFromMap(d.evolutionMensuelle, 'Réclamations', '#0ea5e9');
        this.chargement.reclamations = false;
      },
      error: () => this.chargement.reclamations = false
    });
  }

  // ── Helpers de construction d'options ECharts ───────────────────────
  private lineFromMap(map: { [k: string]: number } | undefined, serieName: string, color: string): EChartsOption {
    const keys = map ? Object.keys(map) : [];
    const values = keys.map(k => map![k]);
    return {
      tooltip: { trigger: 'axis' },
      grid: { left: 40, right: 20, top: 30, bottom: 30 },
      xAxis: { type: 'category', data: keys, axisLine: { lineStyle: { color: '#e2e8f0' } }, axisLabel: { color: '#64748b', fontSize: 11 } },
      yAxis: { type: 'value', splitLine: { lineStyle: { color: '#f1f5f9' } }, axisLabel: { color: '#64748b', fontSize: 11 } },
      series: [{
        name: serieName, type: 'line', smooth: true, data: values,
        lineStyle: { color, width: 2.5 },
        itemStyle: { color },
        areaStyle: { color: { type: 'linear', x: 0, y: 0, x2: 0, y2: 1, colorStops: [
          { offset: 0, color: color + '40' }, { offset: 1, color: color + '00' }
        ]}}
      }]
    };
  }

  private barHorizontalFromList(list: any[], keyName: string, keyValue: string, _title: string, color: string): EChartsOption {
    const labels = (list || []).map(i => i[keyName]);
    const values = (list || []).map(i => i[keyValue]);
    return {
      tooltip: { trigger: 'axis' },
      grid: { left: 120, right: 20, top: 10, bottom: 30 },
      xAxis: { type: 'value', splitLine: { lineStyle: { color: '#f1f5f9' } }, axisLabel: { color: '#64748b' } },
      yAxis: { type: 'category', data: labels, axisLabel: { color: '#475569', fontSize: 11 } },
      series: [{ type: 'bar', data: values, itemStyle: { color, borderRadius: [0, 4, 4, 0] }, barWidth: 16 }]
    };
  }

  private pieFromMap(map: { [k: string]: number } | undefined, _title: string): EChartsOption {
    const data = map ? Object.entries(map).map(([name, value]) => ({ name, value: value as number })) : [];
    return {
      tooltip: { trigger: 'item' },
      legend: { bottom: 0, type: 'scroll' },
      series: [{
        type: 'pie',
        radius: ['45%', '70%'],
        avoidLabelOverlap: true,
        itemStyle: { borderRadius: 6, borderColor: '#fff', borderWidth: 2 },
        label: { show: false }, labelLine: { show: false },
        data
      }]
    };
  }
}
